// Wspólna obsługa listy zasobu w modułach /v2: pobranie, modal rekordu,
// zapis (POST/PATCH) i usuwanie. Strona podaje endpoint i mapowanie pól
// formularza na ciało żądania — resztę robi to miejsce.

import { ref } from 'vue';
import { formatMoney } from '@/composables/useCurrency.js';
import { useApi } from '~/composables/useApi.ts';
import { intlLocale } from '@/composables/useLanguage.js';
import { t } from '@/i18n/index.js';

export function useV2Crud(endpoint) {
    const api = useApi();

    const items = ref([]);
    const loading = ref(true);
    const loadError = ref(false);
    const saving = ref(false);
    const apiErrors = ref([]);
    const flash = ref(null);

    let flashTimer = null;
    const showFlash = (message) => {
        flash.value = message;
        clearTimeout(flashTimer);
        flashTimer = setTimeout(() => { flash.value = null; }, 4000);
    };

    // `query` pozwala zawęzić pobranie (np. wydatki jednego miesiąca), zamiast
    // ściągać całą historię i filtrować ją w przeglądarce.
    const load = async (query = null) => {
        loadError.value = false;
        try {
            items.value = await api.get(endpoint, query ? { query } : {});
        } catch {
            loadError.value = true;
        } finally {
            loading.value = false;
        }
    };

    // Otwarty rekord: bez uuid = nowy.
    const dialog = ref({ show: false, uuid: null, values: {}, title: '' });

    const openCreate = (values, title) => {
        apiErrors.value = [];
        dialog.value = { show: true, uuid: null, values, title };
    };

    const openEdit = (uuid, values, title) => {
        apiErrors.value = [];
        dialog.value = { show: true, uuid, values, title };
    };

    const close = () => { dialog.value = { ...dialog.value, show: false }; };

    const statusOf = (error) => error.statusCode ?? error.status ?? error.response?.status;

    // `toBody` zamienia wartości formularza na to, co przyjmuje API.
    const save = async (values, toBody) => {
        apiErrors.value = [];
        saving.value = true;
        try {
            const body = toBody(values);
            if (dialog.value.uuid) await api.patch(`${endpoint}/${dialog.value.uuid}`, body);
            else await api.post(endpoint, body);
            close();
            await load();
            return true;
        } catch (error) {
            const status = statusOf(error);
            const list = error.data?.errors ?? [];
            if (status === 422 && list.length) apiErrors.value = list;
            else showFlash(t('common.errors.saveRetry'));
            return false;
        } finally {
            saving.value = false;
        }
    };

    // `messages` podmienia komunikat dla konkretnego kodu odpowiedzi — API potrafi
    // odmówić usunięcia (np. 409 na kategorii przypisanej do wydatków) i „nie udało
    // się" nie mówi wtedy, co zrobić.
    const remove = async (uuid = dialog.value.uuid, messages = {}) => {
        if (!uuid) return false;
        saving.value = true;
        try {
            await api.delete(`${endpoint}/${uuid}`);
            close();
            await load();
            return true;
        } catch (error) {
            showFlash(messages[statusOf(error)] ?? t('common.errors.deleteFailed'));
            return false;
        } finally {
            saving.value = false;
        }
    };

    return {
        api, items, loading, loadError, saving, apiErrors, flash, showFlash,
        dialog, load, openCreate, openEdit, close, save, remove,
    };
}

// Pobieranie kilku zasobów naraz tak, żeby awaria jednego nie kasowała widoku.
// Widok dnia składa się z dziewięciu list — gdy API zwróci błąd na treningach
// (np. brak kolumny po wdrożeniu), reszta dnia ma się pokazać normalnie.
export function useResilientLoad(api) {
    const failed = ref([]);

    // Zwraca dane albo `fallback`, zapisując etykietę zasobu, który padł.
    const get = async (url, label, fallback = []) => {
        try {
            return await api.get(url);
        } catch {
            failed.value.push(label);
            return fallback;
        }
    };

    const begin = () => { failed.value = []; };

    return { failed, get, begin };
}

// Grosze → „1 200,00 zł" w walucie konta; kwoty w API są całkowite, w setnych częściach waluty.
export const money = (grosze) => (grosze == null ? '—' : formatMoney(grosze / 100));

// Złotówki z formularza → grosze.
export const toGrosze = (value) => {
    if (value === '' || value == null) return null;
    return Math.round(Number(value) * 100);
};

export const fromGrosze = (grosze) => (grosze == null ? '' : (grosze / 100).toFixed(2));

// Data z API (ISO lub Y-m-d) → „4 sierpnia 2026".
export const dateLabel = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(intlLocale(), { day: 'numeric', month: 'long', year: 'numeric' });
};

const pad = (n) => String(n).padStart(2, '0');
export const ymd = (value) => {
    const d = value instanceof Date ? value : new Date(value);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
